
import { Mixins, Component, Provide, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";
import { DataModules } from "@/store/modules/data";
import Context from "@/mixins/context";
import BrandsStore from "@/mixins/brandsStore";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { IFilter, ISorter } from "@/models/tables";
import { FilterOperators, FilterTypes } from "@/data/table";
import DefaultClientFilters from "@/data/filters/client";

import {
  IAffiliatesProvider,
  IAffiliatesProviderMethods
} from "@/models/providers/affiliates";

@Component({})
export default class AffiliatesProvider extends Mixins(
  Context,
  BrandsStore,
  FilteringSortingTableStore
) {
  // Props

  @Prop({ default: "AFT" })
  public declare id: string;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, string | number | boolean>;

  // Data

  public dataModule: string = DataModules.AFFILIATES;
  public filters: IFilter[] = [
    {
      key: "created_at",
      label: this.$t("_.date_enrolled"),
      type: FilterTypes.DATE,
      operator: FilterOperators.EQUAL,
      group: "affiliate"
    },
    {
      key: "link_visit_count",
      label: this.$t("_.visits"),
      type: FilterTypes.NUMBER,
      operator: FilterOperators.EQUAL,
      group: "affiliate"
    },
    {
      key: "referral_count",
      label: this.$t("_.referrals"),
      type: FilterTypes.NUMBER,
      operator: FilterOperators.EQUAL,
      group: "affiliate"
    }
  ];

  public sorters: ISorter[] = [
    {
      field: "created_at",
      label: this.$t("_.date_enrolled")
    },
    {
      field: "link_visit_count",
      label: this.$t("_.visits")
    },
    {
      field: "referral_count",
      label: this.$t("_.referrals")
    }
  ];

  // Computed

  public get contentIsAvailable() {
    return this.$userCan("list_affiliate_accounts");
  }

  // Methods

  public reloadData() {
    return this.getData({ ...this.config, ignoreStored: true });
  }

  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          with: ["account", "account.clients", "account.clients.image"].join(),
          ...this.manualFilters
        }
      },
      params
    );
  }

  public setClientFilters() {
    this.filters = this.$_.concat(
      this.filters,
      DefaultClientFilters().map(i => ({
        ...i,
        key: `account.clients.${i.key}`,
        label: `${this.$t("_.client")}: ${i.label}`
      }))
    );
  }

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;
    await this.setClientFilters();
    this.getData();
  }

  public render(h: CreateElement) {
    // Guarding
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $affiliatesProvider: this.affiliatesProvider(),
        $affiliatesMethods: this.affiliatesProviderMethods
      })
    );
  }

  // Provide

  @Provide()
  public get affiliatesProviderMethods(): IAffiliatesProviderMethods {
    return {
      applyFilters: this.onFiltersChange,
      applySort: this.onSort,
      getAffiliates: this.getData,
      onPageChange: this.onPageChange,
      onLimitChange: this.onLimitChange,
      getData: this.getData,
      reloadData: this.reloadData
    };
  }

  @Provide()
  public affiliatesProvider(): IAffiliatesProvider {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      affiliates: this.table.data,
      availableFilters: this.filters,
      constQueryFilters: this.constQueryFilters,
      defaultFilters: this.defaults.filters,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      filters: this.filters,
      isLoading: this.table.loading,
      limit: this.table.limit,
      page: this.table.page,
      requestError: this.requestError,
      sorters: this.sorters,
      table: this.table,
      total: this.table.total
    };
  }
}
